import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { Grid, FormControlLabel, Checkbox, Alert } from "@mui/material";
import Button from "components/shared/Button/Button";
import SimpleGrid from "components/shared/SimpleGrid/SimpleGrid";
import {
  BasicText,
  ThresholdContainer,
  ThresholdHeaderText,
  StyledFormGroup,
  StyledButtonDiv,
  StyledGrid,
  StyledStackContainer
} from "../Thresholds/Thresholds.styles";
import EncounterTimer from "components/shared/EncounterTimer/EncounterTimer";
import { useParams } from "react-router";
import Session from "helpers/session";
import {
  AddEncounterEvent,
  associateReadingType
} from "helpers/variables";
import { GlobalContext } from "context/Provider";
import { ThresholdInterface } from "context/initialState/Threshold";
import { useTranslation } from "react-i18next";
import {
  uAbsoluteThreshold,
  uModerateThreshold,
  sThresholdText,
  uHighThreshold
} from "helpers/translationStringConstants";
import {
  generateInputs,
  getThresholds,
  setThresholds,
  removeThresholds,
  GenerateTableHeaders,
  generateList,
  updateThreshold
} from "./Thresholds.utility";
import { useNavigate } from "react-router-dom";

interface ThresholdProps {
  type: string;
}

export default function Thresholds(props: ThresholdProps) {
  const { memberId } = useParams();
  const { threshold, thresholdDispatch, member, config } =
    useContext(GlobalContext);
  const { t } = useTranslation();
  const [highChecked, setHighChecked] = useState<boolean>(false);
  const [lowChecked, setLowChecked] = useState<boolean>(false);
  const [highThreshold, setHighThreshold] = useState<ThresholdInterface>();
  const [moderateThreshold, setModerateThreshold] =
    useState<ThresholdInterface>();
  const [applyDisabled, setApply] = useState<boolean>(true);
  const [showSuccess, setSuccess] = useState<string>("");
  const [totalThresholdList, setTotalThresholdList] = useState<number>(0);
  const [thresholdList, setThresholdList] = useState([]);
  const primarySource = associateReadingType(props.type);
  const navigate = useNavigate();
  console.log("here", props);

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    const paramString = `limit=${itemsPerPage}&page=${currentPage}`;
    getThresholds(memberId, props.type, paramString).then((thresholds) => {
      setThresholdList(thresholds);
    });
  };

  useEffect(() => {
    thresholdDispatch({
      type: "UPDATE",
      payload: { high: {}, moderate: {} }
    });

    getThresholds(memberId, props.type).then((thresholds) => {
      setTotalThresholdList(thresholds.length);
      paginationAction(5, 0);
      if (thresholds.length > 0) {
        const high = thresholds.filter((T: ThresholdInterface) => {
          return T.breachType === "high";
        });
        const low = thresholds.filter((T: ThresholdInterface) => {
          return T.breachType === "moderate";
        });
        setHighChecked(high.length > 0);
        setLowChecked(low.length > 0);
        setHighThreshold(high[0]);
        setModerateThreshold(low[0]);
        if (high.length > 0) {
          setHighChecked(true);
          thresholdDispatch({
            type: "UPDATE",
            payload: { high: high[0].value }
          });
        }
        if (low.length > 0) {
          setLowChecked(true);
          thresholdDispatch({
            type: "UPDATE",
            payload: { moderate: low[0].value }
          });
        }
      }
    });
  }, [props.type]);

  const submitThresholds = async () => {
    const promiseArray: any = [];
    if (lowChecked) {
      // @ts-ignore
          const newSource: any = {};
          newSource.memberId = memberId;
          //TODO: update to use current coordinator
          newSource.coordinator = "6671039a0483b88d7a9943c2";
          newSource.breachType = "moderate";
          newSource.value = threshold?.moderate;
          newSource.readingType = props;
          newSource.assignmentOrigin = "client";
          promiseArray.push(await setThresholds(newSource));
    }
    if (highChecked) {
      // @ts-ignore
          const newSource: any = {};
          newSource.memberId = memberId;
          //TODO: update to use current coordinator
          newSource.coordinator = "6671039a0483b88d7a9943c2";
          newSource.breachType = "high";
          newSource.value = threshold?.high;
          newSource.readingType = props;
          newSource.assignmentOrigin = "client";
          promiseArray.push(await setThresholds(newSource));
    }

    if (!lowChecked && moderateThreshold) {
      // @ts-ignore
      const lowSources = moderateThreshold[0]
      switch (primarySource) {
        case "hrt":
          delete lowSources[0].value.pulse;
          break;
        case "bp":
          delete lowSources[0].value.diastolic;
          delete lowSources[0].value.systolic;
          break;
        case "bgl":
          delete lowSources[0].value.glucose;
          break;
        case "spir":
          delete lowSources[0].value.FVC;
          delete lowSources[0].value.FEV1;
          delete lowSources[0].value.PEF;
          break;
        case "temp":
          delete lowSources[0].value.temp;
          break;
        case "wt":
          delete lowSources[0].value.weight;
          break;
        case "spo2":
          delete lowSources[0].value.spo2;
          break;
        case "activity":
          delete lowSources[0].value.activity;
          break;
      }
      if (Object.keys(lowSources[0].value).length) {
        promiseArray.push(await updateThreshold(lowSources[0]));
      } else {
        promiseArray.push(await removeThresholds(lowSources[0]));
      }
    }
    if (!highChecked && highThreshold) {
      // @ts-ignore
      const highSources = highThreshold[0]
      switch (primarySource) {
        case "hrt":
          delete highSources[0].value.pulse;
          break;
        case "bp":
          delete highSources[0].value.diastolic;
          delete highSources[0].value.systolic;
          break;
        case "bgl":
          delete highSources[0].value.glucose;
          break;
        case "spir":
          delete highSources[0].value.FVC;
          delete highSources[0].value.FEV1;
          delete highSources[0].value.PEF;
          break;
        case "temp":
          delete highSources[0].value.temp;
          break;
        case "wt":
          delete highSources[0].value.weight;
          break;
        case "spo2":
          delete highSources[0].value.spo2;
          break;
        case "activity":
          delete highSources[0].value.activity;
          break;
      }
      if (Object.keys(highSources[0].value).length) {
        promiseArray.push(await updateThreshold(highSources[0]));
      } else {
        promiseArray.push(await removeThresholds(highSources[0]));
      }
    }
    Promise.all(promiseArray).then((promises: any) => {
      setApply(true);
      setSuccess("success");
      thresholdDispatch({
        type: "CLEAR",
        payload: { high: {}, moderate: {} }
      });
      navigate(`/patient/history/timeline/${memberId}`);
    });
    const encounterEvent = AddEncounterEvent(
      "profile change",
      "Threshold updated"
    );
    Session.updateSessionEncounter(
      window.sessionStorage.getItem("member") || "",
      encounterEvent,
      member?.secondsCounter.toString() || ""
    );
  };

  const toggleLow = () => {
    setApply(false);
    setLowChecked(!lowChecked);
  };
  const toggleHigh = () => {
    setApply(false);
    setHighChecked(!highChecked);
  };

  const handleChange = (event: ChangeEvent<HTMLElement>) => {
    setSuccess("");
    setApply(false);
  };

  return (
    <Grid container>
      <Grid item xs={0.4} sm={1} md={1.5} lg={0.5} />
      <Grid item xs={10.8} sm={11} md={10.2} lg={11.4}>
        <StyledStackContainer>
          <EncounterTimer></EncounterTimer>
          <BasicText>{t(sThresholdText)}</BasicText>
          <ThresholdContainer>
            <ThresholdHeaderText>{t(uAbsoluteThreshold)}</ThresholdHeaderText>
            <StyledGrid container>
              <Grid item lg={6}>
                <StyledFormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={lowChecked}
                        onChange={(e) => {
                          toggleLow();
                          handleChange(e);
                        }}
                      />
                    }
                    label={t(uModerateThreshold)}
                  />
                </StyledFormGroup>
                {generateInputs(
                  associateReadingType(props.type),
                  "moderate",
                  !lowChecked,
                  (e: any) => {
                    handleChange(e);
                  }
                )}
              </Grid>
              <Grid item md={6.1} lg={6}>
                <StyledFormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={highChecked}
                        onChange={(e) => {
                          toggleHigh();
                          handleChange(e);
                        }}
                      />
                    }
                    label={t(uHighThreshold)}
                  />
                </StyledFormGroup>
                {generateInputs(
                  associateReadingType(props.type),
                  "high",
                  !highChecked,
                  (e: any) => {
                    handleChange(e);
                  }
                )}
              </Grid>
              <StyledButtonDiv>
                <Button
                  variant="success"
                  onClick={submitThresholds}
                  disabled={applyDisabled}
                >
                  Apply
                </Button>
              </StyledButtonDiv>
            </StyledGrid>
          </ThresholdContainer>
          {showSuccess === "success" ? (
            <Alert severity="success">
              Your configuration changes have been saved
            </Alert>
          ) : (
            ""
          )}
          <SimpleGrid
            // @ts-ignore
            headers={GenerateTableHeaders(props.type)}
            body={generateList(thresholdList)}
            paginationAction={paginationAction}
            totalList={totalThresholdList}
          />
        </StyledStackContainer>
      </Grid>
    </Grid>
  );
}
